import { __decorate, __param } from 'vscode/external/tslib/tslib.es6.js';
import './media/extensionsViewlet.css.js';
import { localize2WithPath, localizeWithPath } from 'vscode/vscode/vs/nls';
import { Delayer, timeout, Promises } from 'vscode/vscode/vs/base/common/async';
import { isCancellationError } from 'vscode/vscode/vs/base/common/errors';
import { createErrorWithActions } from 'vscode/vscode/vs/base/common/errorMessage';
import { Disposable, MutableDisposable } from 'vscode/vscode/vs/base/common/lifecycle';
import { Event } from 'vscode/vscode/vs/base/common/event';
import { Action } from 'vscode/vscode/vs/base/common/actions';
import { append, $, hide, DragAndDropObserver, show, trackFocus, Dimension } from 'vscode/vscode/vs/base/browser/dom';
import { ITelemetryService } from 'vscode/vscode/vs/platform/telemetry/common/telemetry.service';
import { IInstantiationService } from 'vscode/vscode/vs/platform/instantiation/common/instantiation';
import { IExtensionService } from 'vscode/vscode/vs/workbench/services/extensions/common/extensions.service';
import { VIEWLET_ID, CONTEXT_HAS_GALLERY, OUTDATED_EXTENSIONS_VIEW_ID, WORKSPACE_RECOMMENDATIONS_VIEW_ID, extensionsSearchActionsMenu, INSTALL_EXTENSION_FROM_VSIX_COMMAND_ID, AutoCheckUpdatesConfigurationKey, CloseExtensionDetailsOnViewChangeKey } from 'vscode/vscode/vs/workbench/contrib/extensions/common/extensions';
import { IExtensionsWorkbenchService } from 'vscode/vscode/vs/workbench/contrib/extensions/common/extensions.service';
import { InstallLocalExtensionsInRemoteAction, InstallRemoteExtensionsInLocalAction } from 'vscode/vscode/vs/workbench/contrib/extensions/browser/extensionsActions';
import { IExtensionManagementService } from 'vscode/vscode/vs/platform/extensionManagement/common/extensionManagement.service';
import { IExtensionManagementServerService, IWorkbenchExtensionEnablementService } from 'vscode/vscode/vs/workbench/services/extensionManagement/common/extensionManagement.service';
import { ExtensionsInput } from 'vscode/vscode/vs/workbench/contrib/extensions/common/extensionsInput';
import { ServerInstalledExtensionsView, DefaultPopularExtensionsView, DefaultRecommendedExtensionsView, EnabledExtensionsView, DisabledExtensionsView, SearchMarketplaceExtensionsView, ExtensionsListView, RecentlyUpdatedExtensionsView, OutdatedExtensionsView, WorkspaceRecommendedExtensionsView, RecommendedExtensionsView, NONE_CATEGORY, StaticQueryExtensionsView, UntrustedWorkspaceUnsupportedExtensionsView, UntrustedWorkspacePartiallySupportedExtensionsView, VirtualWorkspaceUnsupportedExtensionsView, VirtualWorkspacePartiallySupportedExtensionsView, DeprecatedExtensionsView } from './extensionsViews.js';
import { IProgressService } from 'vscode/vscode/vs/platform/progress/common/progress.service';
import { IEditorGroupsService } from 'vscode/vscode/vs/workbench/services/editor/common/editorGroupsService.service';
import Severity$1 from 'vscode/vscode/vs/base/common/severity';
import { NumberBadge } from 'vscode/vscode/vs/workbench/services/activity/common/activity';
import { IActivityService } from 'vscode/vscode/vs/workbench/services/activity/common/activity.service';
import { IThemeService } from 'vscode/vscode/vs/platform/theme/common/themeService.service';
import { IConfigurationService } from 'vscode/vscode/vs/platform/configuration/common/configuration.service';
import { Extensions } from 'vscode/vscode/vs/workbench/common/views';
import { IViewDescriptorService } from 'vscode/vscode/vs/workbench/common/views.service';
import { IStorageService } from 'vscode/vscode/vs/platform/storage/common/storage.service';
import { IWorkspaceContextService } from 'vscode/vscode/vs/platform/workspace/common/workspace.service';
import { RawContextKey, ContextKeyExpr } from 'vscode/vscode/vs/platform/contextkey/common/contextkey';
import { IContextKeyService } from 'vscode/vscode/vs/platform/contextkey/common/contextkey.service';
import { IContextMenuService } from 'vscode/vscode/vs/platform/contextview/browser/contextView.service';
import { ILogService } from 'vscode/vscode/vs/platform/log/common/log.service';
import { NotificationPriority } from 'vscode/vscode/vs/platform/notification/common/notification';
import { INotificationService } from 'vscode/vscode/vs/platform/notification/common/notification.service';
import { IHostService } from 'vscode/vscode/vs/workbench/services/host/browser/host.service';
import { IWorkbenchLayoutService } from 'vscode/vscode/vs/workbench/services/layout/browser/layoutService.service';
import { ViewPaneContainer } from 'vscode/vscode/vs/workbench/browser/parts/views/viewPaneContainer';
import { Query } from '../common/extensionQuery.js';
import { SuggestEnabledInput } from 'vscode/vscode/vs/workbench/contrib/codeEditor/browser/suggestEnabledInput/suggestEnabledInput';
import { alert } from 'vscode/vscode/vs/base/browser/ui/aria/aria';
import { EXTENSION_CATEGORIES } from 'vscode/vscode/vs/platform/extensions/common/extensions';
import { Registry } from 'vscode/vscode/vs/platform/registry/common/platform';
import { ILabelService } from 'vscode/vscode/vs/platform/label/common/label.service';
import { SyncDescriptor } from 'vscode/vscode/vs/platform/instantiation/common/descriptors';
import { IPreferencesService } from 'vscode/vscode/vs/workbench/services/preferences/common/preferences.service';
import { SIDE_BAR_DRAG_AND_DROP_BACKGROUND } from 'vscode/vscode/vs/workbench/common/theme';
import { IWorkbenchEnvironmentService } from 'vscode/vscode/vs/workbench/services/environment/common/environmentService.service';
import { WorkbenchStateContext, VirtualWorkspaceContext } from 'vscode/vscode/vs/workbench/common/contextkeys';
import { ICommandService } from 'vscode/vscode/vs/platform/commands/common/commands.service';
import { installLocalInRemoteIcon } from 'vscode/vscode/vs/workbench/contrib/extensions/browser/extensionsIcons';
import { registerAction2, Action2, MenuId } from 'vscode/vscode/vs/platform/actions/common/actions';
import { IPaneCompositePartService } from 'vscode/vscode/vs/workbench/services/panecomposite/browser/panecomposite.service';
import { coalesce } from 'vscode/vscode/vs/base/common/arrays';
import { extractEditorsAndFilesDropData } from 'vscode/vscode/vs/platform/dnd/browser/dnd';
import { extname } from 'vscode/vscode/vs/base/common/resources';
import { areSameExtensions } from 'vscode/vscode/vs/platform/extensionManagement/common/extensionManagementUtil';
import { registerNavigableContainer } from 'vscode/vscode/vs/workbench/browser/actions/widgetNavigationCommands';
import { MenuWorkbenchToolBar } from 'vscode/vscode/vs/platform/actions/browser/toolbar';
import { createActionViewItem } from 'vscode/vscode/vs/platform/actions/browser/menuEntryActionViewItem';

const _moduleId = "vs/workbench/contrib/extensions/browser/extensionsViewlet";
const DefaultViewsContext = ( (new RawContextKey('defaultExtensionViews', true)));
const ExtensionsSortByContext = ( (new RawContextKey('extensionsSortByValue', '')));
const SearchMarketplaceExtensionsContext = ( (new RawContextKey('searchMarketplaceExtensions', false)));
const SearchHasTextContext = ( (new RawContextKey('extensionSearchHasText', false)));
const InstalledExtensionsContext = ( (new RawContextKey('installedExtensions', false)));
const SearchInstalledExtensionsContext = ( (new RawContextKey('searchInstalledExtensions', false)));
const SearchRecentlyUpdatedExtensionsContext = ( (new RawContextKey('searchRecentlyUpdatedExtensions', false)));
const SearchExtensionUpdatesContext = ( (new RawContextKey('searchExtensionUpdates', false)));
const SearchOutdatedExtensionsContext = ( (new RawContextKey('searchOutdatedExtensions', false)));
const SearchEnabledExtensionsContext = ( (new RawContextKey('searchEnabledExtensions', false)));
const SearchDisabledExtensionsContext = ( (new RawContextKey('searchDisabledExtensions', false)));
const HasInstalledExtensionsContext = ( (new RawContextKey('hasInstalledExtensions', true)));
const BuiltInExtensionsContext = ( (new RawContextKey('builtInExtensions', false)));
const SearchBuiltInExtensionsContext = ( (new RawContextKey('searchBuiltInExtensions', false)));
const SearchUnsupportedWorkspaceExtensionsContext = ( (new RawContextKey('searchUnsupportedWorkspaceExtensions', false)));
const SearchDeprecatedExtensionsContext = ( (new RawContextKey('searchDeprecatedExtensions', false)));
const RecommendedExtensionsContext = ( (new RawContextKey('recommendedExtensions', false)));
const SortByUpdateDateContext = ( (new RawContextKey('sortByUpdateDate', false)));
const REMOTE_CATEGORY = ( localize2WithPath(_moduleId, 0, "Remote"));
let ExtensionsViewletViewsContribution = class ExtensionsViewletViewsContribution extends Disposable {
    constructor(extensionManagementServerService, labelService, viewDescriptorService, contextKeyService) {
        super();
        this.extensionManagementServerService = extensionManagementServerService;
        this.labelService = labelService;
        this.contextKeyService = contextKeyService;
        this.container = viewDescriptorService.getViewContainerById(VIEWLET_ID);
        this.registerViews();
    }
    registerViews() {
        const viewDescriptors = [];
        viewDescriptors.push(...this.createDefaultExtensionsViewDescriptors());
        viewDescriptors.push(...this.createSearchExtensionsViewDescriptors());
        viewDescriptors.push(...this.createRecommendedExtensionsViewDescriptors());
        viewDescriptors.push(...this.createBuiltinExtensionsViewDescriptors());
        viewDescriptors.push(...this.createUnsupportedWorkspaceExtensionsViewDescriptors());
        viewDescriptors.push(...this.createOtherLocalFilteredExtensionsViewDescriptors());
        ( (Registry.as(Extensions.ViewsRegistry))).registerViews(viewDescriptors, this.container);
    }
    createDefaultExtensionsViewDescriptors() {
        const viewDescriptors = [];
        const servers = [];
        if (this.extensionManagementServerService.localExtensionManagementServer) {
            servers.push(this.extensionManagementServerService.localExtensionManagementServer);
        }
        if (this.extensionManagementServerService.remoteExtensionManagementServer) {
            servers.push(this.extensionManagementServerService.remoteExtensionManagementServer);
        }
        if (this.extensionManagementServerService.webExtensionManagementServer) {
            servers.push(this.extensionManagementServerService.webExtensionManagementServer);
        }
        const getViewName = (viewTitle, server) => {
            return servers.length > 1 ? `${server.label} - ${viewTitle}` : viewTitle;
        };
        let installedWebExtensionsContextChangeEvent = Event.None;
        if (this.extensionManagementServerService.webExtensionManagementServer && this.extensionManagementServerService.remoteExtensionManagementServer) {
            const interestingContextKeys = ( (new Set()));
            interestingContextKeys.add('hasInstalledWebExtensions');
            installedWebExtensionsContextChangeEvent = Event.filter(this.contextKeyService.onDidChangeContext, e => e.affectsSome(interestingContextKeys));
        }
        const serverLabelChangeEvent = Event.any(this.labelService.onDidChangeFormatters, installedWebExtensionsContextChangeEvent);
        for (const server of servers) {
            const getInstalledViewName = () => getViewName(( localizeWithPath(_moduleId, 1, "Installed")), server);
            const onDidChangeTitle = ( (Event.map(serverLabelChangeEvent, () => getInstalledViewName())));
            const id = servers.length > 1 ? `workbench.views.extensions.${server.id}.installed` : `workbench.views.extensions.installed`;
            viewDescriptors.push({
                id,
                get name() {
                    return {
                        value: getInstalledViewName(),
                        original: getViewName('Installed', server)
                    };
                },
                weight: 100,
                order: 1,
                when: ( (ContextKeyExpr.and(DefaultViewsContext))),
                ctorDescriptor: ( (new SyncDescriptor(
                    ServerInstalledExtensionsView,
                    [{ server, flexibleHeight: true, onDidChangeTitle }]
                ))),
                canToggleVisibility: servers.length === 1
            });
            if (server === this.extensionManagementServerService.remoteExtensionManagementServer && this.extensionManagementServerService.localExtensionManagementServer) {
                this._register(registerAction2(class InstallLocalExtensionsInRemoteAction2 extends Action2 {
                    constructor() {
                        super({
                            id: 'workbench.extensions.installLocalExtensions',
                            get title() {
                                return ( localize2WithPath(_moduleId, 2, "Install Local Extensions in '{0}'...", server.label));
                            },
                            category: REMOTE_CATEGORY,
                            icon: installLocalInRemoteIcon,
                            f1: true,
                            menu: {
                                id: MenuId.ViewTitle,
                                when: ( (ContextKeyExpr.equals('view', id))),
                                group: 'navigation',
                            }
                        });
                    }
                    run(accessor) {
                        return accessor.get(IInstantiationService).createInstance(InstallLocalExtensionsInRemoteAction).run();
                    }
                }));
            }
        }
        if (this.extensionManagementServerService.localExtensionManagementServer && this.extensionManagementServerService.remoteExtensionManagementServer) {
            this._register(registerAction2(class InstallRemoteExtensionsInLocalAction2 extends Action2 {
                constructor() {
                    super({
                        id: 'workbench.extensions.actions.installLocalExtensionsInRemote',
                        title: ( localize2WithPath(_moduleId, 3, 'Install Remote Extensions Locally...')),
                        category: REMOTE_CATEGORY,
                        f1: true
                    });
                }
                run(accessor) {
                    return accessor.get(IInstantiationService).createInstance(InstallRemoteExtensionsInLocalAction, 'workbench.extensions.actions.installLocalExtensionsInRemote').run();
                }
            }));
        }
        viewDescriptors.push({
            id: 'workbench.views.extensions.popular',
            name: ( localize2WithPath(_moduleId, 4, "Popular")),
            ctorDescriptor: ( (new SyncDescriptor(DefaultPopularExtensionsView, [{ hideBadge: true }]))),
            when: ( (ContextKeyExpr.and(
                DefaultViewsContext,
                ContextKeyExpr.not('hasInstalledExtensions'),
                CONTEXT_HAS_GALLERY
            ))),
            weight: 60,
            order: 2,
            canToggleVisibility: false
        });
        viewDescriptors.push({
            id: 'extensions.recommendedList',
            name: ( localize2WithPath(_moduleId, 5, "Recommended")),
            ctorDescriptor: ( (new SyncDescriptor(DefaultRecommendedExtensionsView, [{ flexibleHeight: true }]))),
            when: ( (ContextKeyExpr.and(
                DefaultViewsContext,
                 (SortByUpdateDateContext.negate()),
                ContextKeyExpr.not('config.extensions.showRecommendationsOnlyOnDemand'),
                CONTEXT_HAS_GALLERY
            ))),
            weight: 40,
            order: 3,
            canToggleVisibility: true
        });
        if (servers.length === 1) {
            viewDescriptors.push({
                id: 'workbench.views.extensions.enabled',
                name: ( localize2WithPath(_moduleId, 6, "Enabled")),
                ctorDescriptor: ( (new SyncDescriptor(EnabledExtensionsView, [{}]))),
                when: ( (ContextKeyExpr.and(
                    DefaultViewsContext,
                     (ContextKeyExpr.has('hasInstalledExtensions'))
                ))),
                hideByDefault: true,
                weight: 40,
                order: 4,
                canToggleVisibility: true
            });
            viewDescriptors.push({
                id: 'workbench.views.extensions.disabled',
                name: ( localize2WithPath(_moduleId, 7, "Disabled")),
                ctorDescriptor: ( (new SyncDescriptor(DisabledExtensionsView, [{}]))),
                when: ( (ContextKeyExpr.and(
                    DefaultViewsContext,
                     (ContextKeyExpr.has('hasInstalledExtensions'))
                ))),
                hideByDefault: true,
                weight: 10,
                order: 5,
                canToggleVisibility: true
            });
        }
        return viewDescriptors;
    }
    createSearchExtensionsViewDescriptors() {
        const viewDescriptors = [];
        viewDescriptors.push({
            id: 'workbench.views.extensions.marketplace',
            name: ( localize2WithPath(_moduleId, 8, "Marketplace")),
            ctorDescriptor: ( (new SyncDescriptor(SearchMarketplaceExtensionsView, [{}]))),
            when: ( (ContextKeyExpr.and( (ContextKeyExpr.has('searchMarketplaceExtensions'))))),
        });
        viewDescriptors.push({
            id: 'workbench.views.extensions.searchInstalled',
            name: ( localize2WithPath(_moduleId, 1, "Installed")),
            ctorDescriptor: ( (new SyncDescriptor(ExtensionsListView, [{}]))),
            when: ( (ContextKeyExpr.or(
                 (ContextKeyExpr.has('searchInstalledExtensions')),
                 (ContextKeyExpr.has('installedExtensions'))
            ))),
        });
        viewDescriptors.push({
            id: 'workbench.views.extensions.searchRecentlyUpdated',
            name: ( localize2WithPath(_moduleId, 9, "Recently Updated")),
            ctorDescriptor: ( (new SyncDescriptor(RecentlyUpdatedExtensionsView, [{}]))),
            when: ( (ContextKeyExpr.or(
                SearchExtensionUpdatesContext,
                 (ContextKeyExpr.has('searchRecentlyUpdatedExtensions'))
            ))),
            order: 2,
        });
        viewDescriptors.push({
            id: 'workbench.views.extensions.searchEnabled',
            name: ( localize2WithPath(_moduleId, 10, "Enabled")),
            ctorDescriptor: ( (new SyncDescriptor(ExtensionsListView, [{}]))),
            when: ( (ContextKeyExpr.and( (ContextKeyExpr.has('searchEnabledExtensions'))))),
        });
        viewDescriptors.push({
            id: 'workbench.views.extensions.searchDisabled',
            name: ( localize2WithPath(_moduleId, 11, "Disabled")),
            ctorDescriptor: ( (new SyncDescriptor(ExtensionsListView, [{}]))),
            when: ( (ContextKeyExpr.and( (ContextKeyExpr.has('searchDisabledExtensions'))))),
        });
        viewDescriptors.push({
            id: OUTDATED_EXTENSIONS_VIEW_ID,
            name: ( localize2WithPath(_moduleId, 12, "Available Updates")),
            ctorDescriptor: ( (new SyncDescriptor(OutdatedExtensionsView, [{}]))),
            when: ( (ContextKeyExpr.or(
                SearchExtensionUpdatesContext,
                 (ContextKeyExpr.has('searchOutdatedExtensions'))
            ))),
            order: 1,
        });
        viewDescriptors.push({
            id: 'workbench.views.extensions.searchBuiltin',
            name: ( localize2WithPath(_moduleId, 13, "Builtin")),
            ctorDescriptor: ( (new SyncDescriptor(ExtensionsListView, [{}]))),
            when: ( (ContextKeyExpr.and( (ContextKeyExpr.has('searchBuiltInExtensions'))))),
        });
        viewDescriptors.push({
            id: 'workbench.views.extensions.searchWorkspaceUnsupported',
            name: ( localize2WithPath(_moduleId, 14, "Workspace Unsupported")),
            ctorDescriptor: ( (new SyncDescriptor(ExtensionsListView, [{}]))),
            when: ( (ContextKeyExpr.and( (ContextKeyExpr.has('searchWorkspaceUnsupportedExtensions'))))),
        });
        return viewDescriptors;
    }
    createRecommendedExtensionsViewDescriptors() {
        const viewDescriptors = [];
        viewDescriptors.push({
            id: WORKSPACE_RECOMMENDATIONS_VIEW_ID,
            name: ( localize2WithPath(_moduleId, 15, "Workspace Recommendations")),
            ctorDescriptor: ( (new SyncDescriptor(WorkspaceRecommendedExtensionsView, [{}]))),
            when: ( (ContextKeyExpr.and(
                 (ContextKeyExpr.has('recommendedExtensions')),
                 (WorkbenchStateContext.notEqualsTo('empty'))
            ))),
            order: 1
        });
        viewDescriptors.push({
            id: 'workbench.views.extensions.otherRecommendations',
            name: ( localize2WithPath(_moduleId, 16, "Other Recommendations")),
            ctorDescriptor: ( (new SyncDescriptor(RecommendedExtensionsView, [{}]))),
            when: ( (ContextKeyExpr.has('recommendedExtensions'))),
            order: 2
        });
        return viewDescriptors;
    }
    createBuiltinExtensionsViewDescriptors() {
        const viewDescriptors = [];
        const configuredCategories = ['themes', 'programming languages'];
        const otherCategories = EXTENSION_CATEGORIES.filter(c => !configuredCategories.includes(c.toLowerCase()));
        otherCategories.push(NONE_CATEGORY);
        const otherCategoriesQuery = `${( (otherCategories.map(c => `category:"${c}"`))).join(' ')} ${( (configuredCategories.map(c => `category:"-${c}"`))).join(' ')}`;
        viewDescriptors.push({
            id: 'workbench.views.extensions.builtinFeatureExtensions',
            name: ( localize2WithPath(_moduleId, 17, "Features")),
            ctorDescriptor: ( (new SyncDescriptor(StaticQueryExtensionsView, [{ query: `@builtin ${otherCategoriesQuery}` }]))),
            when: ( (ContextKeyExpr.has('builtInExtensions'))),
        });
        viewDescriptors.push({
            id: 'workbench.views.extensions.builtinThemeExtensions',
            name: ( localize2WithPath(_moduleId, 18, "Themes")),
            ctorDescriptor: ( (new SyncDescriptor(StaticQueryExtensionsView, [{ query: `@builtin category:themes` }]))),
            when: ( (ContextKeyExpr.has('builtInExtensions'))),
        });
        viewDescriptors.push({
            id: 'workbench.views.extensions.builtinProgrammingLanguageExtensions',
            name: ( localize2WithPath(_moduleId, 19, "Programming Languages")),
            ctorDescriptor: ( (new SyncDescriptor(
                StaticQueryExtensionsView,
                [{ query: `@builtin category:"programming languages"` }]
            ))),
            when: ( (ContextKeyExpr.has('builtInExtensions'))),
        });
        return viewDescriptors;
    }
    createUnsupportedWorkspaceExtensionsViewDescriptors() {
        const viewDescriptors = [];
        viewDescriptors.push({
            id: 'workbench.views.extensions.untrustedUnsupportedExtensions',
            name: ( localize2WithPath(_moduleId, 20, "Disabled in Restricted Mode")),
            ctorDescriptor: ( (new SyncDescriptor(UntrustedWorkspaceUnsupportedExtensionsView, [{}]))),
            when: ( (ContextKeyExpr.and(SearchUnsupportedWorkspaceExtensionsContext))),
        });
        viewDescriptors.push({
            id: 'workbench.views.extensions.untrustedPartiallySupportedExtensions',
            name: ( localize2WithPath(_moduleId, 21, "Limited in Restricted Mode")),
            ctorDescriptor: ( (new SyncDescriptor(UntrustedWorkspacePartiallySupportedExtensionsView, [{}]))),
            when: ( (ContextKeyExpr.and(SearchUnsupportedWorkspaceExtensionsContext))),
        });
        viewDescriptors.push({
            id: 'workbench.views.extensions.virtualUnsupportedExtensions',
            name: ( localize2WithPath(_moduleId, 22, "Disabled in Virtual Workspaces")),
            ctorDescriptor: ( (new SyncDescriptor(VirtualWorkspaceUnsupportedExtensionsView, [{}]))),
            when: ( (ContextKeyExpr.and(VirtualWorkspaceContext, SearchUnsupportedWorkspaceExtensionsContext))),
        });
        viewDescriptors.push({
            id: 'workbench.views.extensions.virtualPartiallySupportedExtensions',
            name: ( localize2WithPath(_moduleId, 23, "Limited in Virtual Workspaces")),
            ctorDescriptor: ( (new SyncDescriptor(VirtualWorkspacePartiallySupportedExtensionsView, [{}]))),
            when: ( (ContextKeyExpr.and(VirtualWorkspaceContext, SearchUnsupportedWorkspaceExtensionsContext))),
        });
        return viewDescriptors;
    }
    createOtherLocalFilteredExtensionsViewDescriptors() {
        const viewDescriptors = [];
        viewDescriptors.push({
            id: 'workbench.views.extensions.deprecatedExtensions',
            name: ( localize2WithPath(_moduleId, 24, "Deprecated")),
            ctorDescriptor: ( (new SyncDescriptor(DeprecatedExtensionsView, [{}]))),
            when: ( (ContextKeyExpr.and(SearchDeprecatedExtensionsContext))),
        });
        return viewDescriptors;
    }
};
ExtensionsViewletViewsContribution = ( (__decorate([
    ( (__param(0, IExtensionManagementServerService))),
    ( (__param(1, ILabelService))),
    ( (__param(2, IViewDescriptorService))),
    ( (__param(3, IContextKeyService)))
], ExtensionsViewletViewsContribution)));
let ExtensionsViewPaneContainer = class ExtensionsViewPaneContainer extends ViewPaneContainer {
    constructor(layoutService, telemetryService, progressService, instantiationService, editorGroupService, extensionsWorkbenchService, extensionManagementServerService, notificationService, paneCompositeService, themeService, configurationService, storageService, contextService, contextKeyService, contextMenuService, extensionService, viewDescriptorService, preferencesService, commandService) {
        super(VIEWLET_ID, { mergeViewWithContainerWhenSingleView: true }, instantiationService, configurationService, layoutService, contextMenuService, telemetryService, extensionService, themeService, storageService, contextService, viewDescriptorService);
        this.progressService = progressService;
        this.editorGroupService = editorGroupService;
        this.extensionsWorkbenchService = extensionsWorkbenchService;
        this.extensionManagementServerService = extensionManagementServerService;
        this.notificationService = notificationService;
        this.paneCompositeService = paneCompositeService;
        this.contextKeyService = contextKeyService;
        this.preferencesService = preferencesService;
        this.commandService = commandService;
        this.searchDelayer = ( (new Delayer(500)));
        this.defaultViewsContextKey = DefaultViewsContext.bindTo(contextKeyService);
        this.sortByContextKey = ExtensionsSortByContext.bindTo(contextKeyService);
        this.searchMarketplaceExtensionsContextKey = SearchMarketplaceExtensionsContext.bindTo(contextKeyService);
        this.searchHasTextContextKey = SearchHasTextContext.bindTo(contextKeyService);
        this.sortByUpdateDateContextKey = SortByUpdateDateContext.bindTo(contextKeyService);
        this.installedExtensionsContextKey = InstalledExtensionsContext.bindTo(contextKeyService);
        this.searchInstalledExtensionsContextKey = SearchInstalledExtensionsContext.bindTo(contextKeyService);
        this.searchRecentlyUpdatedExtensionsContextKey = SearchRecentlyUpdatedExtensionsContext.bindTo(contextKeyService);
        this.searchExtensionUpdatesContextKey = SearchExtensionUpdatesContext.bindTo(contextKeyService);
        this.searchWorkspaceUnsupportedExtensionsContextKey = SearchUnsupportedWorkspaceExtensionsContext.bindTo(contextKeyService);
        this.searchDeprecatedExtensionsContextKey = SearchDeprecatedExtensionsContext.bindTo(contextKeyService);
        this.searchOutdatedExtensionsContextKey = SearchOutdatedExtensionsContext.bindTo(contextKeyService);
        this.searchEnabledExtensionsContextKey = SearchEnabledExtensionsContext.bindTo(contextKeyService);
        this.searchDisabledExtensionsContextKey = SearchDisabledExtensionsContext.bindTo(contextKeyService);
        this.hasInstalledExtensionsContextKey = HasInstalledExtensionsContext.bindTo(contextKeyService);
        this.builtInExtensionsContextKey = BuiltInExtensionsContext.bindTo(contextKeyService);
        this.searchBuiltInExtensionsContextKey = SearchBuiltInExtensionsContext.bindTo(contextKeyService);
        this.recommendedExtensionsContextKey = RecommendedExtensionsContext.bindTo(contextKeyService);
        this._register(this.paneCompositeService.onDidPaneCompositeOpen(e => { if (e.viewContainerLocation === 0 ) {
            this.onViewletOpen(e.composite);
        } }, this));
        this._register(extensionsWorkbenchService.onReset(() => this.refresh()));
        this.searchViewletState = this.getMemento(1 , 1 );
    }
    get searchValue() {
        return this.searchBox?.getValue();
    }
    create(parent) {
        parent.classList.add('extensions-viewlet');
        this.root = parent;
        const overlay = append(this.root, $('.overlay'));
        const overlayBackgroundColor = this.getColor(SIDE_BAR_DRAG_AND_DROP_BACKGROUND) ?? '';
        overlay.style.backgroundColor = overlayBackgroundColor;
        hide(overlay);
        const header = append(this.root, $('.header'));
        const placeholder = ( localizeWithPath(_moduleId, 25, "Search Extensions in Marketplace"));
        const searchValue = this.searchViewletState['query.value'] ? this.searchViewletState['query.value'] : '';
        const searchContainer = append(header, $('.extensions-search-container'));
        this.searchBox = this._register(this.instantiationService.createInstance(SuggestEnabledInput, `${VIEWLET_ID}.searchbox`, searchContainer, {
            triggerCharacters: ['@'],
            sortKey: (item) => {
                if (item.indexOf(':') === -1) {
                    return 'a';
                }
                else if (/ext:/.test(item) || /id:/.test(item) || /tag:/.test(item)) {
                    return 'b';
                }
                else if (/sort:/.test(item)) {
                    return 'c';
                }
                else {
                    return 'd';
                }
            },
            provideResults: (query) => Query.suggestions(query)
        }, placeholder, 'extensions:searchinput', { placeholderText: placeholder, value: searchValue }));
        this.updateInstalledExtensionsContexts();
        if (this.searchBox.getValue()) {
            this.triggerSearch();
        }
        this._register(this.searchBox.onInputDidChange(() => {
            this.sortByContextKey.set(Query.parse(this.searchBox?.getValue() ?? '').sortBy);
            this.triggerSearch();
        }, this));
        this._register(this.searchBox.onShouldFocusResults(() => this.focusListView(), this));
        const controlElement = append(searchContainer, $('.extensions-search-actions-container'));
        this._register(this.instantiationService.createInstance(MenuWorkbenchToolBar, controlElement, extensionsSearchActionsMenu, {
            toolbarOptions: {
                primaryGroup: () => true,
            },
            actionViewItemProvider: (action, options) => createActionViewItem(this.instantiationService, action, options)
        }));
        this._register(( (new DragAndDropObserver(this.root, {
            onDragEnter: (e) => {
                if (this.isSupportedDragElement(e)) {
                    show(overlay);
                }
            },
            onDragLeave: (e) => {
                if (this.isSupportedDragElement(e)) {
                    hide(overlay);
                }
            },
            onDragOver: (e) => {
                if (this.isSupportedDragElement(e)) {
                    e.dataTransfer.dropEffect = 'copy';
                }
            },
            onDrop: async (e) => {
                if (this.isSupportedDragElement(e)) {
                    hide(overlay);
                    const vsixs = coalesce(( ((await this.instantiationService.invokeFunction(accessor => extractEditorsAndFilesDropData(accessor, e)))
                        .map(
                        editor => editor.resource && extname(editor.resource) === '.vsix' ? editor.resource : undefined
                    ))));
                    if (vsixs.length > 0) {
                        try {
                            await this.commandService.executeCommand(INSTALL_EXTENSION_FROM_VSIX_COMMAND_ID, vsixs);
                        }
                        catch (err) {
                            this.notificationService.error(err);
                        }
                    }
                }
            }
        }))));
        super.create(append(this.root, $('.extensions')));
        const focusTracker = this._register(trackFocus(this.root));
        const isSearchBoxFocused = () => this.searchBox?.inputWidget.hasWidgetFocus();
        this._register(registerNavigableContainer({
            name: 'extensionsView',
            focusNotifiers: [focusTracker],
            focusNextWidget: () => {
                if (isSearchBoxFocused()) {
                    this.focusListView();
                }
            },
            focusPreviousWidget: () => {
                if (!isSearchBoxFocused()) {
                    this.searchBox?.focus();
                }
            }
        }));
    }
    focus() {
        super.focus();
        this.searchBox?.focus();
    }
    layout(dimension) {
        if (this.root) {
            this.root.classList.toggle('narrow', dimension.width <= 250);
            this.root.classList.toggle('mini', dimension.width <= 200);
        }
        this.searchBox?.layout(( (new Dimension(dimension.width - 34 -  8 - (24 * 2), 20))));
        super.layout(( (new Dimension(dimension.width, dimension.height - 41))));
    }
    getOptimalWidth() {
        return 400;
    }
    search(value) {
        if (this.searchBox && this.searchBox.getValue() !== value) {
            this.searchBox.setValue(value);
        }
    }
    async refresh() {
        await this.updateInstalledExtensionsContexts();
        this.doSearch(true);
        if (this.configurationService.getValue(AutoCheckUpdatesConfigurationKey)) {
            this.extensionsWorkbenchService.checkForUpdates();
        }
    }
    async updateInstalledExtensionsContexts() {
        const result = await this.extensionsWorkbenchService.queryLocal();
        this.hasInstalledExtensionsContextKey.set(( (result.some(r => !r.isBuiltin))));
    }
    triggerSearch() {
        this.searchDelayer.trigger(() => this.doSearch(), this.searchBox && this.searchBox.getValue() ? 500 : 0).then(undefined, err => this.onError(err));
    }
    normalizedQuery() {
        return this.searchBox
            ? this.searchBox.getValue()
                .trim()
                .replace(/@category/g, 'category')
                .replace(/@tag:/g, 'tag:')
                .replace(/@ext:/g, 'ext:')
                .replace(/@featured/g, 'featured')
                .replace(/@popular/g, this.extensionManagementServerService.webExtensionManagementServer && !this.extensionManagementServerService.localExtensionManagementServer && !this.extensionManagementServerService.remoteExtensionManagementServer ? '@web' : '@popular')
            : '';
    }
    saveState() {
        const value = this.searchBox ? this.searchBox.getValue() : '';
        if (ExtensionsListView.isLocalExtensionsQuery(value)) {
            this.searchViewletState['query.value'] = value;
        }
        else {
            this.searchViewletState['query.value'] = '';
        }
        super.saveState();
    }
    doSearch(refresh) {
        const value = this.normalizedQuery();
        this.contextKeyService.bufferChangeEvents(() => {
            const isRecommendedExtensionsQuery = ExtensionsListView.isRecommendedExtensionsQuery(value);
            this.searchHasTextContextKey.set(value.trim() !== '');
            this.installedExtensionsContextKey.set(ExtensionsListView.isInstalledExtensionsQuery(value));
            this.searchInstalledExtensionsContextKey.set(ExtensionsListView.isSearchInstalledExtensionsQuery(value));
            this.searchRecentlyUpdatedExtensionsContextKey.set(ExtensionsListView.isSearchRecentlyUpdatedQuery(value) && !ExtensionsListView.isSearchExtensionUpdatesQuery(value));
            this.searchOutdatedExtensionsContextKey.set(ExtensionsListView.isOutdatedExtensionsQuery(value) && !ExtensionsListView.isSearchExtensionUpdatesQuery(value));
            this.searchExtensionUpdatesContextKey.set(ExtensionsListView.isSearchExtensionUpdatesQuery(value));
            this.searchEnabledExtensionsContextKey.set(ExtensionsListView.isEnabledExtensionsQuery(value));
            this.searchDisabledExtensionsContextKey.set(ExtensionsListView.isDisabledExtensionsQuery(value));
            this.searchBuiltInExtensionsContextKey.set(ExtensionsListView.isSearchBuiltInExtensionsQuery(value));
            this.searchWorkspaceUnsupportedExtensionsContextKey.set(ExtensionsListView.isSearchWorkspaceUnsupportedExtensionsQuery(value));
            this.searchDeprecatedExtensionsContextKey.set(ExtensionsListView.isSearchDeprecatedExtensionsQuery(value));
            this.builtInExtensionsContextKey.set(ExtensionsListView.isBuiltInExtensionsQuery(value));
            this.recommendedExtensionsContextKey.set(isRecommendedExtensionsQuery);
            this.searchMarketplaceExtensionsContextKey.set(!!value && !ExtensionsListView.isLocalExtensionsQuery(value) && !isRecommendedExtensionsQuery);
            this.sortByUpdateDateContextKey.set(ExtensionsListView.isSortUpdateDateQuery(value));
            this.defaultViewsContextKey.set(!value || ExtensionsListView.isSortInstalledExtensionsQuery(value));
        });
        return this.progress(Promise.all(( (this.panes.map(view => view.show(this.normalizedQuery(), refresh)
            .then(model => this.alertSearchResult(model.length, view.id))))))).then(() => undefined);
    }
    onDidAddViewDescriptors(added) {
        const addedViews = super.onDidAddViewDescriptors(added);
        this.progress(Promise.all(( (addedViews.map(addedView => addedView.show(this.normalizedQuery())
            .then(model => this.alertSearchResult(model.length, addedView.id)))))));
        return addedViews;
    }
    alertSearchResult(count, viewId) {
        const view = this.viewContainerModel.visibleViewDescriptors.find(view => view.id === viewId);
        switch (count) {
            case 0:
                break;
            case 1:
                if (view) {
                    alert(( localizeWithPath(_moduleId, 26, "1 extension found in the {0} section.", view.name.value)));
                }
                else {
                    alert(( localizeWithPath(_moduleId, 27, "1 extension found.")));
                }
                break;
            default:
                if (view) {
                    alert(( localizeWithPath(
                        _moduleId,
                        28,
                        "{0} extensions found in the {1} section.",
                        count,
                        view.name.value
                    )));
                }
                else {
                    alert(( localizeWithPath(_moduleId, 29, "{0} extensions found.", count)));
                }
                break;
        }
    }
    getFirstExpandedPane() {
        for (const pane of this.panes) {
            if (pane.isExpanded() && pane instanceof ExtensionsListView) {
                return pane;
            }
        }
        return undefined;
    }
    focusListView() {
        const pane = this.getFirstExpandedPane();
        if (pane && pane.count() > 0) {
            pane.focus();
        }
    }
    onViewletOpen(viewlet) {
        if (!viewlet || viewlet.getId() === VIEWLET_ID) {
            return;
        }
        if (this.configurationService.getValue(CloseExtensionDetailsOnViewChangeKey)) {
            const promises = ( (this.editorGroupService.groups.map(group => {
                const editors = group.editors.filter(input => input instanceof ExtensionsInput);
                return group.closeEditors(editors);
            })));
            Promise.all(promises);
        }
    }
    progress(promise) {
        return this.progressService.withProgress({ location: 5  }, () => promise);
    }
    onError(err) {
        if (isCancellationError(err)) {
            return;
        }
        const message = err && err.message || '';
        if (/ECONNREFUSED/.test(message)) {
            const error = createErrorWithActions(( localizeWithPath(
                _moduleId,
                30,
                "Marketplace returned 'ECONNREFUSED'. Please check the 'http.proxy' setting."
            )), [
                ( (new Action('open user settings', ( localizeWithPath(_moduleId, 31, "Open User Settings")), undefined, true, () => this.preferencesService.openUserSettings())))
            ]);
            this.notificationService.error(error);
            return;
        }
        this.notificationService.error(err);
    }
    isSupportedDragElement(e) {
        if (e.dataTransfer) {
            const typesLowerCase = ( (e.dataTransfer.types.map(t => t.toLocaleLowerCase())));
            return typesLowerCase.indexOf('files') !== -1;
        }
        return false;
    }
};
ExtensionsViewPaneContainer = ( (__decorate([
    ( (__param(0, IWorkbenchLayoutService))),
    ( (__param(1, ITelemetryService))),
    ( (__param(2, IProgressService))),
    ( (__param(3, IInstantiationService))),
    ( (__param(4, IEditorGroupsService))),
    ( (__param(5, IExtensionsWorkbenchService))),
    ( (__param(6, IExtensionManagementServerService))),
    ( (__param(7, INotificationService))),
    ( (__param(8, IPaneCompositePartService))),
    ( (__param(9, IThemeService))),
    ( (__param(10, IConfigurationService))),
    ( (__param(11, IStorageService))),
    ( (__param(12, IWorkspaceContextService))),
    ( (__param(13, IContextKeyService))),
    ( (__param(14, IContextMenuService))),
    ( (__param(15, IExtensionService))),
    ( (__param(16, IViewDescriptorService))),
    ( (__param(17, IPreferencesService))),
    ( (__param(18, ICommandService)))
], ExtensionsViewPaneContainer)));
let StatusUpdater = class StatusUpdater extends Disposable {
    constructor(activityService, extensionsWorkbenchService, extensionEnablementService) {
        super();
        this.activityService = activityService;
        this.extensionsWorkbenchService = extensionsWorkbenchService;
        this.extensionEnablementService = extensionEnablementService;
        this.badgeHandle = this._register(( (new MutableDisposable())));
        this.onServiceChange();
        this._register(Event.debounce(extensionsWorkbenchService.onChange, () => undefined, 100, undefined, undefined, undefined, this._store)(this.onServiceChange, this));
    }
    onServiceChange() {
        this.badgeHandle.clear();
        const actionRequired = this.extensionsWorkbenchService.installed.filter(e => e.runtimeState !== undefined);
        const outdated = this.extensionsWorkbenchService.outdated.reduce((r, e) => r + (this.extensionEnablementService.isEnabled(e.local) && !actionRequired.includes(e) ? 1 : 0), 0);
        const newBadgeNumber = outdated + actionRequired.length;
        if (newBadgeNumber > 0) {
            let msg = '';
            if (outdated) {
                msg += outdated === 1 ? ( localizeWithPath(_moduleId, 32, '{0} requires update', outdated)) : ( localizeWithPath(_moduleId, 33, '{0} require update', outdated));
            }
            if (outdated > 0 && actionRequired.length > 0) {
                msg += ', ';
            }
            if (actionRequired.length) {
                msg += actionRequired.length === 1 ? ( localizeWithPath(_moduleId, 34, '{0} requires restart', actionRequired.length)) : ( localizeWithPath(_moduleId, 35, '{0} require restart', actionRequired.length));
            }
            const badge = ( (new NumberBadge(newBadgeNumber, () => msg)));
            this.badgeHandle.value = this.activityService.showViewContainerActivity(VIEWLET_ID, { badge });
        }
    }
};
StatusUpdater = ( (__decorate([
    ( (__param(0, IActivityService))),
    ( (__param(1, IExtensionsWorkbenchService))),
    ( (__param(2, IWorkbenchExtensionEnablementService)))
], StatusUpdater)));
let MaliciousExtensionChecker = class MaliciousExtensionChecker {
    constructor(extensionsManagementService, hostService, logService, notificationService, environmentService) {
        this.extensionsManagementService = extensionsManagementService;
        this.hostService = hostService;
        this.logService = logService;
        this.notificationService = notificationService;
        this.environmentService = environmentService;
        if (!this.environmentService.disableExtensions) {
            this.loopCheckForMaliciousExtensions();
        }
    }
    loopCheckForMaliciousExtensions() {
        this.checkForMaliciousExtensions()
            .then(() => timeout(1000 * 60 * 5))
            .then(() => this.loopCheckForMaliciousExtensions());
    }
    checkForMaliciousExtensions() {
        return this.extensionsManagementService.getExtensionsControlManifest().then(extensionsControlManifest => {
            return this.extensionsManagementService.getInstalled(1 ).then(installed => {
                const maliciousExtensions = installed
                    .filter(e => ( (extensionsControlManifest.malicious.some(identifier => areSameExtensions(e.identifier, identifier)))));
                if (maliciousExtensions.length) {
                    return Promises.settled(( (maliciousExtensions.map(e => this.extensionsManagementService.uninstall(e).then(() => {
                        this.notificationService.prompt(Severity$1.Warning, ( localizeWithPath(
                            _moduleId,
                            36,
                            "We have uninstalled '{0}' which was reported to be problematic.",
                            e.identifier.id
                        )), [{
                                label: ( localizeWithPath(_moduleId, 37, "Reload Now")),
                                run: () => this.hostService.reload()
                            }], {
                            sticky: true,
                            priority: NotificationPriority.URGENT
                        });
                    })))));
                }
                else {
                    return Promise.resolve(undefined);
                }
            }).then(() => undefined);
        }, err => this.logService.error(err));
    }
};
MaliciousExtensionChecker = ( (__decorate([
    ( (__param(0, IExtensionManagementService))),
    ( (__param(1, IHostService))),
    ( (__param(2, ILogService))),
    ( (__param(3, INotificationService))),
    ( (__param(4, IWorkbenchEnvironmentService)))
], MaliciousExtensionChecker)));

export { BuiltInExtensionsContext, DefaultViewsContext, ExtensionsSortByContext, ExtensionsViewPaneContainer, ExtensionsViewletViewsContribution, MaliciousExtensionChecker, RecommendedExtensionsContext, SearchHasTextContext, SearchMarketplaceExtensionsContext, StatusUpdater };
